/**
 * notificationService.js
 *
 * ══════════════════════════════════════════════════════════════════
 *  HOW AUTOMATIC EMAIL SENDING WORKS — FULL ARCHITECTURE
 * ══════════════════════════════════════════════════════════════════
 *
 *  The app already evaluates ALL conditions (rule engine, deadline engine,
 *  YTD metrics engine) and writes results to the SharePoint AlertsLog list.
 *  Power Automate watches that ONE list — no logic duplication needed.
 *
 *  ┌──────────────────────────────────────────────────────────────┐
 *  │  EVENTS COVERED (all alert types the app generates):         │
 *  │   • Rule condition not met  (any dependency rule)            │
 *  │   • CSF not performing      (CSF status bad)                 │
 *  │   • Production target missed (YTD < target)                  │
 *  │   • Project deadline approaching (within panic period)       │
 *  │   • Project deadline overdue                                 │
 *  └──────────────────────────────────────────────────────────────┘
 *
 *  SENDING MODES:
 *
 *  A) IN-APP "Send Now"  (this file)
 *     • The app already ran the full rule engine on load.
 *     • This service reads those results and sends via Graph API.
 *     • Covers ALL above events with full context.
 *     • Use the "Send Now" button on the Notifications page.
 *
 *  B) EVENT-DRIVEN via Power Automate  ← recommended primary mode
 *     • Trigger: SharePoint "When an item is created" on AlertsLog.
 *     • Every time the app (or any process) writes a new alert →
 *       Power Automate wakes up immediately and sends the email.
 *     • No schedule needed — fires on every new alert automatically.
 *     • Covers ALL event types because AlertsLog gets every app alert.
 *
 *  C) SCHEDULED DIGEST via Power Automate  ← recommended secondary
 *     • Trigger: Recurrence (Mon & Thu 8am or as configured).
 *     • Queries AlertsLog for unresolved alerts in the last N days.
 *     • Groups by entity → sends one digest email per owner.
 *     • Catches anything the event-driven flow might have missed.
 *
 * ══════════════════════════════════════════════════════════════════
 *  SHAREPOINT COLUMNS TO ADD to your Entities list:
 *    • OwnerEmail      — Single line of text  (owner's email address)
 *    • SupervisorEmail — Single line of text  (supervisor's email)
 *
 *  SHAREPOINT COLUMNS TO ADD to your AlertsLog list:
 *    • OwnerEmail      — Single line of text  (copy from entity on write)
 *    • SupervisorEmail — Single line of text  (copy from entity on write)
 *    • EntityTitle     — Single line of text  (entity name for email subject)
 *    • AssetTitle      — Single line of text  (asset name)
 *    • EmailSent       — Yes/No (default No) — PA marks this Yes after sending
 *
 *  AZURE AD PERMISSIONS for in-app send:
 *    • Mail.Send (Delegated) — Azure portal → App registrations →
 *      API permissions → Microsoft Graph → Delegated → Mail.Send
 *      → Grant admin consent
 * ══════════════════════════════════════════════════════════════════
 */

// ── Constants ────────────────────────────────────────────────────────────────

const GRAPH_SEND_MAIL = "https://graph.microsoft.com/v1.0/me/sendMail";
export const MAIL_SEND_SCOPE = ["Mail.Send"];

const GOOD_STATUSES = new Set([
  "Met",
  "On Track",
  "On-Track",
  "Completed",
  "Active",
]);
const CRITICAL_THRESHOLD = 50;

// ── Settings ─────────────────────────────────────────────────────────────────

export const DEFAULT_SETTINGS = {
  panicDays: 30,
  enableCSF: true,
  enableDeadlines: true,
  enableMetricMiss: true,
  enableRuleFailures: true,
  enableAutoPrompt: false,
  scheduleDays: [1, 4], // 1=Mon, 4=Thu
  scheduleTime: "08:00",
};

export function isNotificationDue(type, scheduleDays = [1, 4], lastSent = {}) {
  const today = new Date();
  if (!scheduleDays.includes(today.getDay())) return false;
  const lastDate = lastSent[type];
  if (!lastDate) return true;
  const last = new Date(lastDate);
  return (
    last.getFullYear() !== today.getFullYear() ||
    last.getMonth() !== today.getMonth() ||
    last.getDate() !== today.getDate()
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  BUILD NOTIFICATION PAYLOADS
//  Each builder reads the already-evaluated allAssetsOverview from the store
//  (ruleEngine has already run, so no logic duplication here).
// ══════════════════════════════════════════════════════════════════════════════

/**
 * 1. NON-PERFORMING CSFs
 *    A CSF is flagged when it is the SOURCE of at least one unmet rule.
 *    Email: CSF Owner (To) + Supervisor (CC)
 */
export function buildCSFNotifications(allAssetsOverview) {
  const csfMap = new Map();

  for (const {
    asset,
    entities,
    ruleEvaluations,
    latestSignals,
  } of allAssetsOverview) {
    const unmetCSF = (ruleEvaluations || []).filter(
      (r) => !r.conditionMet && r.sourceEntityType === "CSF",
    );

    for (const rule of unmetCSF) {
      const csfId = Number(rule.sourceEntityId);
      const csf = entities.find((e) => Number(e.Id) === csfId);
      if (!csf) continue;

      const target = entities.find(
        (e) => Number(e.Id) === Number(rule.targetEntityId),
      );
      const signal = latestSignals?.get(csfId);

      if (!csfMap.has(csfId)) {
        csfMap.set(csfId, {
          kind: "csf",
          entity: csf,
          assetName: asset.Title,
          ownerEmail: csf.OwnerEmail || null,
          supervisorEmail: csf.SupervisorEmail || null,
          owner: csf.Owner || null,
          currentStatus: signal?.Status || csf.ytdStatus || "Unknown",
          reason: signal?.Reason || null,
          affectedEntities: [],
        });
      }

      if (target) {
        csfMap.get(csfId).affectedEntities.push({
          title: target.Title,
          type: target.EntityType,
          ruleType: rule.ruleType,
          priority: rule.priority ?? 10,
        });
      }
    }
  }

  return [...csfMap.values()];
}

/**
 * 2. PRODUCTION METRIC TARGET MISSED
 *    Fires when ytdStatus === 'Not Met' (target miss is always RED in the app).
 *    Email: Metric Owner (To) + Supervisor (CC)
 */
export function buildMetricMissNotifications(allAssetsOverview) {
  const results = [];

  for (const { asset, entities, latestSignals } of allAssetsOverview) {
    const metrics = entities.filter(
      (e) =>
        e.EntityType === "Metric" &&
        !(e.Category || "").toLowerCase().includes("hse") &&
        e.ytdStatus === "Not Met",
    );

    for (const m of metrics) {
      const signal = latestSignals?.get(m.Id);
      results.push({
        kind: "metric",
        entity: m,
        assetName: asset.Title,
        ownerEmail: m.OwnerEmail || null,
        supervisorEmail: m.SupervisorEmail || null,
        owner: m.Owner || null,
        currentStatus: m.ytdStatus,
        ytdAverage: m.ytdAverage ?? null,
        targetValue: m.TargetValue ?? null,
        targetUnit: m.TargetUnit || "",
        signalStatus: signal?.Status || null,
        reason: signal?.Reason || null,
      });
    }
  }

  return results;
}

/**
 * 3. RULE CONDITIONS NOT MET (target entity perspective)
 *    Notifies the OWNER of the entity whose rules are failing —
 *    i.e. the entity being blocked/affected, not the source.
 *    Only fires for critical unmet rules (priority < 50) to avoid noise.
 *    Deduplicates: one email per target entity.
 *    Email: Target Entity Owner (To) + Supervisor (CC)
 */
export function buildRuleFailureNotifications(allAssetsOverview) {
  const entityMap = new Map();

  for (const {
    asset,
    entities,
    ruleEvaluations,
    latestSignals,
  } of allAssetsOverview) {
    // Group unmet critical rules by target entity
    const criticalUnmet = (ruleEvaluations || []).filter(
      (r) => !r.conditionMet && (r.priority ?? 10) < CRITICAL_THRESHOLD,
    );

    for (const rule of criticalUnmet) {
      const targetId = Number(rule.targetEntityId);
      const target = entities.find((e) => Number(e.Id) === targetId);
      if (!target) continue;

      // Only include production metrics and projects (not CSF, not HSE)
      const isRelevant =
        target.EntityType === "Project" ||
        (target.EntityType === "Metric" &&
          !(target.Category || "").toLowerCase().includes("hse"));
      if (!isRelevant) continue;

      const srcEntity = entities.find(
        (e) => Number(e.Id) === Number(rule.sourceEntityId),
      );
      const srcSignal = latestSignals?.get(Number(rule.sourceEntityId));

      if (!entityMap.has(targetId)) {
        const targetSignal = latestSignals?.get(targetId);
        entityMap.set(targetId, {
          kind: "rule",
          entity: target,
          assetName: asset.Title,
          ownerEmail: target.OwnerEmail || null,
          supervisorEmail: target.SupervisorEmail || null,
          owner: target.Owner || null,
          reason: targetSignal?.Reason || null,
          failingRules: [],
        });
      }

      entityMap.get(targetId).failingRules.push({
        sourceTitle: srcEntity?.Title || rule.sourceEntity || "Unknown",
        sourceType: rule.sourceEntityType || "Entity",
        sourceStatus: srcSignal?.Status || srcEntity?.ytdStatus || "—",
        sourceOwner: srcEntity?.Owner || null,
        ruleType: rule.ruleType,
        priority: rule.priority ?? 10,
        conditionExpression: rule.conditionExpression || null,
      });
    }
  }

  return [...entityMap.values()];
}

/**
 * 4. PROJECT DEADLINES — approaching or overdue
 *    Fires when daysRemaining <= panicDays OR isOverdue.
 *    Email: Project Owner (To) + Supervisor (CC)
 */
export function buildDeadlineNotifications(allAssetsOverview, panicDays = 30) {
  const results = [];

  for (const { asset, entities, latestSignals } of allAssetsOverview) {
    const projects = entities.filter((e) => e.EntityType === "Project");

    for (const p of projects) {
      const ds = p.deadlineStatus;
      if (!ds) continue;

      const status = typeof ds === "object" ? ds.status : ds;
      const daysRemaining =
        typeof ds === "object" ? (ds.daysRemaining ?? null) : null;
      const isOverdue = typeof ds === "object" ? !!ds.isOverdue : false;

      if (!status || status === "none" || status === "no-deadline") continue;
      if (!isOverdue && (daysRemaining === null || daysRemaining > panicDays))
        continue;

      // Skip if already completed per signal
      const signal = latestSignals?.get(p.Id);
      if (GOOD_STATUSES.has(signal?.Status)) continue;

      results.push({
        kind: "deadline",
        entity: p,
        assetName: asset.Title,
        ownerEmail: p.OwnerEmail || null,
        supervisorEmail: p.SupervisorEmail || null,
        owner: p.Owner || null,
        daysRemaining,
        isOverdue,
        deadline: p.Deadline || p.EndDate || p.DueDate || null,
        currentStatus: signal?.Status || status,
        reason: signal?.Reason || null,
      });
    }
  }

  return results;
}

/**
 * Convenience: build ALL notification types at once.
 */
export function buildAllNotifications(allAssetsOverview, settings) {
  return {
    csf: settings.enableCSF ? buildCSFNotifications(allAssetsOverview) : [],
    metrics: settings.enableMetricMiss
      ? buildMetricMissNotifications(allAssetsOverview)
      : [],
    rules: settings.enableRuleFailures
      ? buildRuleFailureNotifications(allAssetsOverview)
      : [],
    deadlines: settings.enableDeadlines
      ? buildDeadlineNotifications(allAssetsOverview, settings.panicDays)
      : [],
  };
}

// ══════════════════════════════════════════════════════════════════════════════
//  EMAIL HTML TEMPLATES
// ══════════════════════════════════════════════════════════════════════════════

function baseTemplate(title, accentColor, bodyHtml) {
  const sentAt = new Date().toLocaleString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
           background: #f1f5f9; color: #1e293b; }
    .wrap  { max-width: 620px; margin: 24px auto; background: #fff;
             border-radius: 14px; overflow: hidden;
             box-shadow: 0 4px 32px rgba(0,0,0,0.10); }
    .hdr   { background: ${accentColor}; padding: 24px 28px; color: #fff; }
    .hdr h1 { font-size: 18px; font-weight: 700; margin-bottom: 3px; }
    .hdr p  { font-size: 12px; opacity: 0.85; }
    .bdy   { padding: 24px 28px; }
    .bdy p  { font-size: 14px; color: #475569; line-height: 1.65; margin-bottom: 12px; }
    .card  { border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 13px 15px;
             margin: 10px 0; background: #f8fafc; }
    .card h3 { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 4px; }
    .card p  { font-size: 13px; color: #64748b; margin: 0; }
    .card.alert-red   { background: #fff5f5; border-color: #fecaca; }
    .card.alert-amber { background: #fffbeb; border-color: #fde68a; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 99px;
             font-size: 11px; font-weight: 700; margin-right: 3px; }
    .b-red    { background: #fee2e2; color: #dc2626; }
    .b-amber  { background: #fef3c7; color: #d97706; }
    .b-blue   { background: #dbeafe; color: #2563eb; }
    .b-purple { background: #ede9fe; color: #7c3aed; }
    .b-grey   { background: #f1f5f9; color: #64748b; }
    .row  { display: flex; align-items: center; gap: 8px; padding: 6px 0;
            border-bottom: 1px solid #f1f5f9; font-size: 13px; }
    .row:last-child { border-bottom: none; }
    .row .name { flex: 1; }
    hr   { border: none; border-top: 1px solid #e2e8f0; margin: 16px 0; }
    .ftr { padding: 13px 28px; background: #f8fafc;
           border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="hdr">
      <h1>${title}</h1>
      <p>Technical Dashboard — Automated Alert</p>
    </div>
    <div class="bdy">${bodyHtml}</div>
    <div class="ftr">Sent: ${sentAt} &nbsp;·&nbsp; Technical Dashboard Notification System</div>
  </div>
</body>
</html>`;
}

// ── Template: CSF ──────────────────────────────────────────────────────────

export function composeCSFEmail(n) {
  const criticalCount = n.affectedEntities.filter(
    (a) => a.priority < CRITICAL_THRESHOLD,
  ).length;
  const rows = n.affectedEntities
    .sort((a, b) => a.priority - b.priority)
    .map(
      (a) => `
      <div class="row">
        <span class="badge ${a.type === "Metric" ? "b-blue" : "b-purple"}">${a.type}</span>
        <span class="name">${a.title}</span>
        <span class="badge ${a.priority < CRITICAL_THRESHOLD ? "b-red" : "b-amber"}">P${a.priority}</span>
        <span class="badge b-grey">${a.ruleType || "Rule"}</span>
      </div>`,
    )
    .join("");

  const body = `
    <p>The Critical Success Factor below, which is under your responsibility, is
       <strong>not performing</strong> and is currently blocking operational targets.</p>
    <div class="card alert-red">
      <h3>⭐ ${n.entity.Title}</h3>
      <p>Asset: <strong>${n.assetName}</strong> &nbsp;·&nbsp;
         Owner: <strong>${n.owner || "N/A"}</strong> &nbsp;·&nbsp;
         Status: <strong style="color:#dc2626">${n.currentStatus}</strong></p>
      ${n.reason ? `<p style="margin-top:8px;color:#b91c1c"><strong>Reason:</strong> ${n.reason}</p>` : ""}
    </div>
    <hr>
    <p><strong>${n.affectedEntities.length} affected</strong>${criticalCount > 0 ? ` &mdash; <span class="badge b-red">${criticalCount} critical impact(s)</span>` : ""}</p>
    ${rows}
    <hr>
    <p>Please take immediate corrective action and update the CSF status in the system.</p>`;

  return {
    subject: `[CSF Alert] "${n.entity.Title}" is not performing — ${n.assetName}`,
    html: baseTemplate("CSF Performance Alert 🔴", "#dc2626", body),
  };
}

// ── Template: Metric miss ──────────────────────────────────────────────────

export function composeMetricEmail(n) {
  const pct =
    n.targetValue && n.ytdAverage !== null
      ? ((n.ytdAverage / n.targetValue) * 100).toFixed(1)
      : null;

  const body = `
    <p>The production metric below has <strong>missed its target</strong> and requires your immediate attention.</p>
    <div class="card alert-red">
      <h3>📈 ${n.entity.Title}</h3>
      <p>Asset: <strong>${n.assetName}</strong> &nbsp;·&nbsp; Owner: <strong>${n.owner || "N/A"}</strong></p>
      ${
        n.targetValue !== null
          ? `
        <p style="margin-top:8px">
          Target: <strong>${Number(n.targetValue).toLocaleString()} ${n.targetUnit}</strong>
          ${n.ytdAverage !== null ? `&nbsp;·&nbsp; YTD Average: <strong style="color:#dc2626">${Number(n.ytdAverage).toLocaleString()} ${n.targetUnit}</strong>` : ""}
          ${pct !== null ? `&nbsp;·&nbsp; Achievement: <strong style="color:#dc2626">${pct}%</strong>` : ""}
        </p>`
          : ""
      }
      ${n.reason ? `<p style="margin-top:8px;color:#b91c1c"><strong>Reason:</strong> ${n.reason}</p>` : ""}
    </div>
    <hr>
    <p>Please review the contributing factors and update your corrective action plan.</p>`;

  return {
    subject: `[Metric Alert] Target Missed: "${n.entity.Title}" — ${n.assetName}`,
    html: baseTemplate("Production Target Missed 🔴", "#dc2626", body),
  };
}

// ── Template: Rule failure (target entity owner) ───────────────────────────

export function composeRuleFailureEmail(n) {
  const rows = n.failingRules
    .sort((a, b) => a.priority - b.priority)
    .map(
      (r) => `
      <div class="row">
        <span class="badge b-grey">${r.sourceType}</span>
        <span class="name">${r.sourceTitle}${r.sourceOwner ? ` <em style="color:#94a3b8;font-size:11px">(${r.sourceOwner})</em>` : ""}</span>
        <span class="badge b-red">P${r.priority}</span>
        <span style="font-size:12px;color:#64748b">Status: ${r.sourceStatus}</span>
      </div>`,
    )
    .join("");

  const type = n.entity.EntityType;
  const icon = type === "Project" ? "🏗" : "📈";

  const body = `
    <p>The following ${type.toLowerCase()} has <strong>${n.failingRules.length} critical dependency rule${n.failingRules.length !== 1 ? "s" : ""} not met</strong>, causing it to show RED status on the Operations Dashboard.</p>
    <div class="card alert-red">
      <h3>${icon} ${n.entity.Title}</h3>
      <p>Asset: <strong>${n.assetName}</strong> &nbsp;·&nbsp; Owner: <strong>${n.owner || "N/A"}</strong></p>
      ${n.reason ? `<p style="margin-top:8px;color:#b91c1c"><strong>Reason:</strong> ${n.reason}</p>` : ""}
    </div>
    <hr>
    <p><strong>Failing dependencies (critical):</strong></p>
    ${rows}
    <hr>
    <p>Please follow up with the owners of the failing dependencies listed above, or escalate if required.</p>`;

  return {
    subject: `[Rule Alert] Critical dependencies not met: "${n.entity.Title}" — ${n.assetName}`,
    html: baseTemplate("Critical Rule Conditions Not Met 🔴", "#dc2626", body),
  };
}

// ── Template: Project deadline ──────────────────────────────────────────────

export function composeDeadlineEmail(n, panicDays) {
  const color = n.isOverdue
    ? "#dc2626"
    : n.daysRemaining !== null && n.daysRemaining <= 7
      ? "#ea580c"
      : "#d97706";
  const title = n.isOverdue
    ? "Project Deadline Overdue 🚨"
    : `Project Deadline Alert — ${n.daysRemaining ?? "?"} days remaining ⏰`;
  const dlStr = n.deadline
    ? new Date(n.deadline).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "Not set";

  const body = `
    <p>${
      n.isOverdue
        ? "The project below has <strong>passed its deadline</strong> and requires immediate escalation."
        : `This project is within the <strong>${panicDays}-day alert window</strong>. Please ensure all deliverables are on track.`
    }</p>
    <div class="card ${n.isOverdue ? "alert-red" : "alert-amber"}">
      <h3>🏗 ${n.entity.Title}</h3>
      <p>Asset: <strong>${n.assetName}</strong> &nbsp;·&nbsp; Owner: <strong>${n.owner || "N/A"}</strong></p>
      <p style="margin-top:8px">
        Deadline: <strong>${dlStr}</strong>
        ${
          n.daysRemaining !== null
            ? n.isOverdue
              ? `&nbsp;·&nbsp; <span class="badge b-red">OVERDUE by ${Math.abs(n.daysRemaining)} day(s)</span>`
              : `&nbsp;·&nbsp; <span class="badge b-amber">${n.daysRemaining} days left</span>`
            : ""
        }
      </p>
      ${n.reason ? `<p style="margin-top:8px;color:#b91c1c"><strong>Reason:</strong> ${n.reason}</p>` : ""}
    </div>
    <hr>
    <p>${
      n.isOverdue
        ? "Please escalate immediately. Update the project record with a revised completion date or close-out status."
        : "If the project will complete on time, please update the status in the system to avoid further alerts."
    }</p>`;

  return {
    subject: n.isOverdue
      ? `[OVERDUE] Project: "${n.entity.Title}" — ${n.assetName}`
      : `[Deadline Alert] "${n.entity.Title}" due in ${n.daysRemaining} days — ${n.assetName}`,
    html: baseTemplate(title, color, body),
  };
}

// ══════════════════════════════════════════════════════════════════════════════
//  GRAPH API SEND
// ══════════════════════════════════════════════════════════════════════════════

export async function sendEmail({
  accessToken,
  to,
  cc = [],
  subject,
  htmlBody,
}) {
  const toList = to.filter(Boolean);
  const ccList = cc.filter(Boolean);
  if (toList.length === 0 && ccList.length === 0)
    throw new Error("No recipients.");

  const resp = await fetch(GRAPH_SEND_MAIL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      message: {
        subject,
        body: { contentType: "HTML", content: htmlBody },
        toRecipients: toList.map((a) => ({ emailAddress: { address: a } })),
        ...(ccList.length > 0 && {
          ccRecipients: ccList.map((a) => ({ emailAddress: { address: a } })),
        }),
      },
      saveToSentItems: true,
    }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Graph API ${resp.status}`);
  }
}

/**
 * Acquire Mail.Send token via MSAL then send ALL notification types.
 * Returns { sent, skipped, errors, timestamp }
 */
export async function sendAllNotifications({
  msalInstance,
  account,
  notifications,
  panicDays,
}) {
  let accessToken;
  try {
    const r = await msalInstance.acquireTokenSilent({
      scopes: MAIL_SEND_SCOPE,
      account,
    });
    accessToken = r.accessToken;
  } catch {
    const r = await msalInstance.acquireTokenPopup({ scopes: MAIL_SEND_SCOPE });
    accessToken = r.accessToken;
  }

  const { csf = [], metrics = [], rules = [], deadlines = [] } = notifications;
  let sent = 0;
  const errors = [];
  const now = new Date().toISOString();

  const tryEmail = async (to, cc, subject, html, label) => {
    if (!to[0] && !cc[0]) {
      errors.push(`No email address for: ${label}`);
      return;
    }
    try {
      await sendEmail({ accessToken, to, cc, subject, htmlBody: html });
      sent++;
    } catch (e) {
      errors.push(`${label}: ${e.message}`);
    }
  };

  const lastSentUpdates = {};

  for (const n of csf) {
    const { subject, html } = composeCSFEmail(n);
    await tryEmail(
      [n.ownerEmail],
      [n.supervisorEmail],
      subject,
      html,
      n.entity.Title,
    );
  }
  if (csf.length) lastSentUpdates.csf = now;

  for (const n of metrics) {
    const { subject, html } = composeMetricEmail(n);
    await tryEmail(
      [n.ownerEmail],
      [n.supervisorEmail],
      subject,
      html,
      n.entity.Title,
    );
  }
  if (metrics.length) lastSentUpdates.metrics = now;

  for (const n of rules) {
    const { subject, html } = composeRuleFailureEmail(n);
    await tryEmail(
      [n.ownerEmail],
      [n.supervisorEmail],
      subject,
      html,
      n.entity.Title,
    );
  }
  if (rules.length) lastSentUpdates.rules = now;

  for (const n of deadlines) {
    const { subject, html } = composeDeadlineEmail(n, panicDays);
    await tryEmail(
      [n.ownerEmail],
      [n.supervisorEmail],
      subject,
      html,
      n.entity.Title,
    );
  }
  if (deadlines.length) lastSentUpdates.deadline = now;

  const total = csf.length + metrics.length + rules.length + deadlines.length;
  return {
    sent,
    skipped: Math.max(0, total - sent - errors.length),
    errors,
    timestamp: now,
    lastSentUpdates,
  };
}
